// Автоверификации («Верификация-3 Автоматика») — зеркало портала 73. ТОЛЬКО ЧТЕНИЕ.
//
// ЗАЧЕМ ЭТОТ ЭКРАН. Ручка ядра `/p73/auto-verifications` работала с самого переноса
// инженерного блока, а раздела не было: 39 записей лежали в зеркале и не были видны никому.
// Дешевле этого в очереди переноса нет ничего — данные есть, права есть, не хватало экрана.
//
// ЧЕМ ЭТА ФОРМА ОТЛИЧАЕТСЯ ОТ ОБЫЧНОЙ ВЕРИФИКАЦИИ, и почему это не «ещё одна вкладка»:
//   • она НЕ уходит в Bitrix вовсе — ни задачи, ни комментария в сделку. Так решено на
//     портале 12.06.2026 и записано у него в коде (autover.js:2). У обычной верификации
//     при одобрении улетает и то и другое;
//   • у неё нет ни одной фотографии: реестр `p73_autover_photos` пуст (0 строк, замер
//     27.07.2026). Поэтому ключ R2 этому разделу не нужен, а пустая колонка «Фото» должна
//     быть ОБЪЯСНЕНА, а не показана прочерком;
//   • у неё нет утверждения: все 39 записей в состоянии «черновик».
// Каждое из трёх — повод, по которому человек решит, что CRM что-то потеряла. Поэтому они
// сказаны словами на самом экране, а не только здесь.
//
// КАРТОЧКИ У РАЗДЕЛА НЕТ. Ядро её и не отдаёт: `/p73/auto-verifications` — единственная
// ручка этой формы, развёрнутого `data` в ответе нет. Придумывать карточку поверх списка
// значило бы показать пустой чек-лист и выдать это за содержание проверки.
import { useMemo, useState } from 'react'
import { Link, useSearchParams } from 'react-router-dom'

import { useRefs } from '../api/hooks'
import { useАвтоверификации, useP73Refs } from '../api/p73'
import type { АвтоверификацияСтрока, ФильтрыP73 } from '../api/p73'
import { EmptySource } from '../components/EmptySource'
import { EntityLink } from '../components/EntityLink'
import { FilterBar } from '../components/FilterBar'
import { QueryState } from '../components/QueryState'
import { fmtDate } from '../lib/format'
import { ПУТИ, названиеСтатуса, процент, видСтатуса } from '../lib/p73'
import { ПРО_АВТОВЕРИФИКАЦИИ, РАЗМЕР_СТРАНИЦЫ } from '../lib/p73due'

export default function AutoVerifications() {
  const [sp, setSp] = useSearchParams()
  // Судно и сделка живут в адресе: сюда будут вести ссылки из карточек, и такую ссылку
  // должно быть можно переслать. Тот же приём, что в разделе верификаций.
  const vesselD1 = sp.get('vessel') || ''
  const dealD1 = sp.get('deal') || ''

  const [q, setQ] = useState('')
  const [status, setStatus] = useState('')
  const [engineer, setEngineer] = useState('')
  const [start, setStart] = useState(0)

  const refs = useRefs()
  const p73 = useP73Refs()

  const фильтры = useMemo<ФильтрыP73>(() => ({
    q: q.trim() || undefined,
    status: status || undefined,
    engineer: engineer || undefined,
    vessel: vesselD1 || undefined,
    deal: dealD1 || undefined,
    start,
    limit: РАЗМЕР_СТРАНИЦЫ,
  }), [q, status, engineer, vesselD1, dealD1, start])

  const запрос = useАвтоверификации(фильтры)
  const данные = запрос.data
  const счёт = p73.data?.counts

  const сСброса = <T,>(setter: (v: T) => void) => (v: T) => { setStart(0); setter(v) }

  const снять = (ключ: string) => {
    const next = new URLSearchParams(sp)
    next.delete(ключ)
    setStart(0)
    setSp(next, { replace: true })
  }

  // Ноль фотографий по ВСЕМУ разделу — факт из ответа ядра, а не догадка по странице:
  // считаем по строкам, которые пришли, и подпись показываем только если их и правда ноль.
  const фотоВсего = (данные?.rows ?? []).reduce((n, v) => n + (v.photos_total || 0), 0)

  return (
    <>
      <div className="page-head">
        <h1>Автоверификации</h1>
        <span className="spacer" />
        <span className="hint">
          {данные ? `найдено: ${данные.total}` : ''}
          {счёт ? ` · всего в ядре: ${счёт.auto_verifications}` : ''}
        </span>
      </div>

      <div className="notice">
        <b>В Bitrix эта форма не уходит.</b> {ПРО_АВТОВЕРИФИКАЦИИ}
      </div>

      <div className="card" style={{ marginBottom: 16 }}>
        <FilterBar id="auto-verifications">
          <div className="f" style={{ flex: 2, minWidth: 220 }}>
            <label htmlFor="avq">Судно (название или IMO)</label>
            <input id="avq" value={q} onChange={(e) => сСброса(setQ)(e.target.value)}
                   placeholder="например: Дон Стар или 9142904" />
          </div>

          <div className="f">
            <label htmlFor="avst">Статус</label>
            <select id="avst" value={status} onChange={(e) => сСброса(setStatus)(e.target.value)}>
              <option value="">любой</option>
              {/* Свой справочник статусов ядро по этой форме не отдаёт: в данных одно
                  значение, и заводить ради него ещё один запрос незачем. */}
              <option value="draft">Черновик</option>
              <option value="submitted">На утверждении</option>
              <option value="approved">Утверждён</option>
            </select>
          </div>

          <div className="f" style={{ minWidth: 200 }}>
            <label htmlFor="aven">Инженер</label>
            <select id="aven" value={engineer} onChange={(e) => сСброса(setEngineer)(e.target.value)}
                    disabled={!p73.data}>
              <option value="">любой</option>
              {(p73.data?.engineers ?? []).map((u) => (
                <option key={u.d1_id} value={u.d1_id}>
                  {u.full_name || u.username || `#${u.d1_id}`}{u.is_active ? '' : ' (не работает)'}
                </option>
              ))}
            </select>
          </div>
        </FilterBar>

        {(vesselD1 || dealD1) && (
          <div className="hint" style={{ marginTop: 10 }}>
            Отфильтровано по{' '}
            {vesselD1 && <>судну портала #{vesselD1} <button onClick={() => снять('vessel')}>снять</button>{' '}</>}
            {dealD1 && <>заявке портала #{dealD1} <button onClick={() => снять('deal')}>снять</button></>}
          </div>
        )}

        <div className="hint" style={{ marginTop: 10 }}>
          Данные — зеркало портала 73 (обновляется синком). Заполняют форму инженеры на
          портале; этот раздел ничего не пишет. Обычные верификации судов —{' '}
          <Link to={ПУТИ.верификации}>в соседнем разделе</Link>, это разные формы и разные записи.
        </div>
      </div>

      <QueryState isLoading={запрос.isLoading} error={запрос.error}>
        <div className="card">
          {/* `cards` — на телефоне таблица разбирается на карточки (styles.css, §12).
              Подписи колонок продублированы в data-label: шапки там на экране нет. */}
          <div className="table-wrap cards">
            <table>
              <thead>
                <tr>
                  <th className="nowrap">ID</th>
                  <th>Судно</th>
                  <th>Заявка / сделка</th>
                  <th className="nowrap">Инженер</th>
                  <th className="nowrap">Статус</th>
                  <th className="num">Заполнено</th>
                  <th className="num">Фото</th>
                  <th className="nowrap">Изменена</th>
                </tr>
              </thead>
              <tbody>
                {(данные?.rows ?? []).map((v) => (
                  <Строка key={v.d1_id} v={v} имяСотрудника={refs.userNameOr} />
                ))}
                {данные && данные.rows.length === 0 && (
                  <tr>
                    <td colSpan={8} className="empty">
                      <div className="empty-title">Под эти фильтры автоверификаций нет</div>
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>

          {данные && данные.total > РАЗМЕР_СТРАНИЦЫ && (
            <div className="pager">
              <button disabled={start === 0 || запрос.isFetching}
                      onClick={() => setStart(Math.max(0, start - РАЗМЕР_СТРАНИЦЫ))}>← назад</button>
              <span className="muted">
                {start + 1}–{Math.min(start + РАЗМЕР_СТРАНИЦЫ, данные.total)} из {данные.total}
              </span>
              <button disabled={данные.next === null || запрос.isFetching}
                      onClick={() => setStart(данные.next!)}>вперёд →</button>
            </div>
          )}
        </div>

        {/* Пустая колонка «Фото» — это не потерянные файлы. Реестр `p73_autover_photos`
            пуст в источнике, и молчать об этом нельзя: человек, привыкший к фотографиям
            обычных верификаций, решит, что их не перенесли. */}
        {данные && данные.rows.length > 0 && (
          <div className="card" style={{ marginTop: 12 }}>
            <EmptySource
              что="Фотографий автоверификаций"
              строк={фотоВсего}
              источник="портал 73"
              таблица="p73_autover_photos"
              почему="у этой формы фотографий нет ни одной с самого её запуска — снимков она не просит"
            >
              Ключ к хранилищу снимков этому разделу поэтому и не нужен.
            </EmptySource>
          </div>
        )}
      </QueryState>
    </>
  )
}

function Строка({ v, имяСотрудника }: {
  v: АвтоверификацияСтрока
  имяСотрудника: (id?: string | number | null, fallback?: string | null) => string
}) {
  return (
    <tr>
      {/* Карточки у этой формы нет — ядро не отдаёт её содержимое. Показываем номер,
          по которому запись находится на портале, и не делаем ссылку в никуда. */}
      <td className="mono nowrap" data-label="ID">{v.d1_id}</td>
      <td data-label="Судно">
        <EntityLink kind="vessel" id={v.vessel?.bitrix_id} title={v.vessel?.name}
                    empty={v.vessel?.name || '— нет мостика к контакту'} />
        {v.vessel?.imo && <div className="muted mono">IMO {v.vessel.imo}</div>}
      </td>
      <td data-label="Заявка / сделка"
          style={{ maxWidth: 260, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
        {v.deal?.bitrix_id
          ? <EntityLink kind="deal" id={v.deal.bitrix_id} title={v.deal.title} />
          : <span className="muted">сделки Bitrix нет</span>}
      </td>
      <td className="nowrap" data-label="Инженер">{имяСотрудника(v.engineer?.bitrix_id, v.engineer?.name)}</td>
      <td className="nowrap" data-label="Статус">
        <span className={`badge ${видСтатуса(v.status)}`}>{названиеСтатуса(v.status)}</span>
      </td>
      <td className="num" data-label="Заполнено">{процент(v.fill_pct)}</td>
      {/* Ноль здесь — посчитанный ноль, а не «не знаем»: ядро считает фотографии запросом
          по реестру и всегда отдаёт числом. Прочерк читался бы как «не смогли посмотреть». */}
      <td className="num" data-label="Фото">
        {v.photos_total > 0
          ? v.photos_total
          : <span className="muted" title="у этой формы фотографий нет вовсе">0</span>}
      </td>
      <td className="nowrap muted" data-label="Изменена">{fmtDate(v.updated_at || v.created_at, true)}</td>
    </tr>
  )
}
